// Send Media Library photos/videos to people by email or text.
// Backend: netlify/functions/media-share.js. Each recipient gets their own link
// (cpx-prototype.netlify.app/m/<token>) to a gallery page with full-size downloads.
import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Mail, MessageSquare, Loader2, Plus, CheckCircle2, AlertCircle, Search } from 'lucide-react';
import { getAccessToken } from '../services/auth';

const FN = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/media-share'
  : '/.netlify/functions/media-share';

async function call(action, params = {}) {
  const token = getAccessToken();
  const res = await fetch(FN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ action, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const thumb = (key) => `/.netlify/functions/r2-image?key=${encodeURIComponent(key)}`;
const SOURCE_LABEL = { outlook: 'Outlook contact', sent: 'You emailed', mail: 'In your email', saved: 'Saved', recent: 'Sent before' };

// items = [] is "Ask for photos" mode: nothing to send, just an upload link (Jess, Oct 20, 2026).
export default function SendMediaModal({ items, onClose }) {
  const askOnly = items.length === 0;
  const [askUpload, setAskUpload] = useState(askOnly);
  const [eventLabel, setEventLabel] = useState('');
  const [q, setQ] = useState('');
  const [people, setPeople] = useState([]);
  const [outlookContacts, setOutlookContacts] = useState(true);
  const [searching, setSearching] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newPerson, setNewPerson] = useState({ name: '', email: '', phone: '', save: true });
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState(askOnly ? 'Could you send us your photos?' : 'Your pictures from The Crab Place');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const searchSeq = useRef(0);

  const nVideos = items.filter((i) => i.media_kind === 'video').length;
  const nPhotos = items.length - nVideos;
  const what = [nPhotos ? `${nPhotos} photo${nPhotos === 1 ? '' : 's'}` : '', nVideos ? `${nVideos} video${nVideos === 1 ? '' : 's'}` : ''].filter(Boolean).join(' and ');

  useEffect(() => {
    const seq = ++searchSeq.current;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const d = await call('contacts', { q });
        if (seq !== searchSeq.current) return;
        setPeople(d.people || []);
        setOutlookContacts(!!d.outlookContacts);
      } catch (e) {
        if (seq === searchSeq.current) setError(e.message);
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const addRecipient = (p) => {
    const key = (p.email || '') + '|' + (p.phone || '');
    if (recipients.some((r) => r.key === key)) return;
    setRecipients((prev) => [...prev, { ...p, key, via: p.phone ? 'text' : 'email' }]);
    setQ('');
  };
  const setVia = (key, via, patch = {}) => setRecipients((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch, via } : r)));
  const removeRecipient = (key) => setRecipients((prev) => prev.filter((r) => r.key !== key));

  const addNew = async () => {
    setError(null);
    const { name, email, phone, save } = newPerson;
    if (!email.trim() && !phone.trim()) { setError('Enter an email address or a mobile number.'); return; }
    let person = { name: name.trim(), email: email.trim().toLowerCase() || null, phone: phone.trim() || null, phoneDisplay: phone.trim() || null, source: 'saved' };
    if (save) {
      try { const d = await call('save_contact', { name, email, phone }); person = d.contact; }
      catch (e) { setError(e.message); return; }
    }
    addRecipient(person);
    setNewPerson({ name: '', email: '', phone: '', save: true });
    setAdding(false);
  };

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const d = await call('send', {
        assetIds: items.map((i) => i.id),
        recipients: recipients.map((r) => ({ name: r.name, email: r.email, phone: r.phone, via: r.via })),
        message, subject, askUpload, eventLabel,
      });
      setResults(d.results || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const anyEmail = recipients.some((r) => r.via === 'email');
  const anyText = recipients.some((r) => r.via === 'text');

  return (
    <div onClick={() => { if (!busy) onClose(); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 'min(640px, 100%)', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--gray-200)' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>{askOnly ? 'Ask for their photos' : `Send ${what}`}</div>
          <button onClick={onClose} disabled={busy} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
        </div>

        <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {askOnly ? null : <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
            {items.slice(0, 14).map((i) => (
              <img key={i.id} src={thumb(i.r2_thumb_key)} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flex: '0 0 auto' }} />
            ))}
            {items.length > 14 ? <div style={{ flex: '0 0 auto', width: 56, height: 56, borderRadius: 6, background: 'var(--gray-100, #f1f1f3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--gray-600, #555)' }}>+{items.length - 14}</div> : null}
          </div>}

          {results ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {results.map((r, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14 }}>
                  {r.ok ? <CheckCircle2 size={18} color="#1a7f37" style={{ flex: '0 0 auto' }} /> : <AlertCircle size={18} color="#c33" style={{ flex: '0 0 auto' }} />}
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.ok ? `${r.via === 'text' ? 'Texted' : 'Emailed'} ${r.to}` : `Not sent to ${r.to}`}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{r.ok ? r.detail : r.error}</div>
                    {r.ok && r.link ? <a href={r.link} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>Open the page they got</a> : null}
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
                <button onClick={() => setResults(null)} style={btnSecondary}>Send to someone else</button>
                <button onClick={onClose} style={btnPrimary}>Done</button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <div style={label}>To</div>
                {recipients.length ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                    {recipients.map((r) => (
                      <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name || r.email || r.phoneDisplay}</div>
                          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{r.via === 'email' ? r.email : r.phoneDisplay}</div>
                        </div>
                        <ViaToggle r={r} onChange={(via, patch) => setVia(r.key, via, patch)} />
                        <button onClick={() => removeRecipient(r.key)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div style={{ position: 'relative' }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--gray-500)' }} />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or phone" style={{ ...input, paddingLeft: 30 }} autoFocus />
                </div>
                <div style={{ border: '1px solid var(--gray-200)', borderTop: 'none', borderRadius: '0 0 8px 8px', maxHeight: 200, overflowY: 'auto' }}>
                  {searching && !people.length ? <div style={{ padding: 10, fontSize: 13, color: 'var(--gray-500)' }}><Loader2 size={13} className="spin" /> Looking…</div> : null}
                  {!searching && !people.length ? <div style={{ padding: 10, fontSize: 13, color: 'var(--gray-500)' }}>Nobody found in your email or saved people. If they are only in your phone, use Add someone new.</div> : null}
                  {people.map((p, idx) => (
                    <button key={idx} onClick={() => addRecipient(p)} style={{ display: 'flex', width: '100%', textAlign: 'left', gap: 8, alignItems: 'center', padding: '7px 10px', background: 'none', border: 'none', borderBottom: '1px solid var(--gray-100, #f1f1f3)', cursor: 'pointer' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{[p.email, p.phoneDisplay].filter(Boolean).join(' · ')}</div>
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{SOURCE_LABEL[p.source] || ''}</span>
                    </button>
                  ))}
                </div>
                {!outlookContacts ? (
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>Showing people you have emailed. Your Outlook contacts (with phone numbers) show up once Contacts.Read is granted on the CPx Microsoft app.</div>
                ) : null}

                {adding ? (
                  <div style={{ marginTop: 10, padding: 10, border: '1px solid var(--gray-200)', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input value={newPerson.name} onChange={(e) => setNewPerson({ ...newPerson, name: e.target.value })} placeholder="Name" style={input} />
                    <input value={newPerson.email} onChange={(e) => setNewPerson({ ...newPerson, email: e.target.value })} placeholder="Email address" type="email" style={input} />
                    <input value={newPerson.phone} onChange={(e) => setNewPerson({ ...newPerson, phone: e.target.value })} placeholder="Mobile number for texts" type="tel" style={input} />
                    <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={newPerson.save} onChange={(e) => setNewPerson({ ...newPerson, save: e.target.checked })} /> Save them for next time (everyone on the team will see them here)
                    </label>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button onClick={() => setAdding(false)} style={btnSecondary}>Cancel</button>
                      <button onClick={addNew} style={btnPrimary}>Add</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setAdding(true)} style={{ ...btnSecondary, marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5 }}><Plus size={13} /> Add someone new</button>
                )}
              </div>

              <div>
                {!askOnly ? (
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
                    <input type="checkbox" checked={askUpload} onChange={(e) => setAskUpload(e.target.checked)} />
                    Ask them to send theirs too (their page gets an "Add your photos" button)
                  </label>
                ) : null}
                {askUpload ? (
                  <div style={{ marginBottom: 12 }}>
                    <div style={label}>Event</div>
                    <input value={eventLabel} onChange={(e) => setEventLabel(e.target.value)} placeholder="Oct 18 photo shoot" style={{ ...input, borderColor: eventLabel.trim() ? 'var(--gray-200)' : 'var(--orange)' }} />
                    <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>Required. Shown to them, and tagged on everything they send so you can find it in the Media Library. You, Jess and Greg get an email when they upload.</div>
                  </div>
                ) : null}
                <div style={label}>Note (optional)</div>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder={askOnly ? 'Thanks for coming out Saturday! Could you send us the photos you took?' : 'Thanks for helping out on Saturday. Here are the pictures.'} style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              {anyEmail ? (
                <div>
                  <div style={label}>Email subject</div>
                  <input value={subject} onChange={(e) => setSubject(e.target.value)} style={input} />
                </div>
              ) : null}
              {anyText ? (
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Texts go out from The Crab Place's Dotdigital text line and start with your first name, so they know who it is from. Replies to the text don't come back to you.</div>
              ) : null}
              {anyEmail ? (
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Emails go from your own Outlook and land in your Sent Items.</div>
              ) : null}

              {error ? <div style={{ fontSize: 13, color: '#c33' }}>{error}</div> : null}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={onClose} disabled={busy} style={btnSecondary}>Cancel</button>
                <button onClick={send} disabled={busy || !recipients.length || (askUpload && !eventLabel.trim())} title={askUpload && !eventLabel.trim() ? 'Name the event first' : undefined} style={{ ...btnPrimary, opacity: busy || !recipients.length || (askUpload && !eventLabel.trim()) ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {busy ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Send{recipients.length > 1 ? ` to ${recipients.length} people` : ''}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Email/Text switch. A person with only an email can't be texted (and vice versa),
// but a number can be typed in on the spot.
function ViaToggle({ r, onChange }) {
  const [editing, setEditing] = useState(null); // 'email' | 'text'
  const [val, setVal] = useState('');
  const pick = (via) => {
    const have = via === 'email' ? r.email : r.phone;
    if (have) { onChange(via); return; }
    setEditing(via); setVal('');
  };
  if (editing) {
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} placeholder={editing === 'email' ? 'Email address' : 'Mobile number'} style={{ ...input, width: 150, padding: '4px 6px', fontSize: 12 }} />
        <button style={{ ...btnPrimary, padding: '4px 8px', fontSize: 12 }} onClick={() => {
          if (!val.trim()) return;
          const v = val.trim();
          onChange(editing, editing === 'email' ? { email: v.toLowerCase() } : { phone: v, phoneDisplay: v });
          setEditing(null);
        }}>OK</button>
      </div>
    );
  }
  const seg = (via, Icon, text) => (
    <button onClick={() => pick(via)} title={via === 'email' ? (r.email || 'Add an email address') : (r.phoneDisplay || 'Add a mobile number')}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
        background: r.via === via ? 'var(--navy)' : '#fff', color: r.via === via ? '#fff' : 'var(--navy)' }}>
      <Icon size={12} /> {text}
    </button>
  );
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--navy)', borderRadius: 16, overflow: 'hidden', flex: '0 0 auto' }}>
      {seg('email', Mail, 'Email')}
      {seg('text', MessageSquare, 'Text')}
    </div>
  );
}

const label = { fontSize: 11, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 };
const input = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 14, border: '1px solid var(--gray-200)', borderRadius: 8 };
const btnPrimary = { padding: '8px 14px', fontSize: 13, fontWeight: 700, background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const btnSecondary = { padding: '8px 14px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#1d1d1f', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer' };
